/**
 * A ajuda contextual do sistema (D-203, DS-698): um ícone discreto ao lado do título do
 * grupo, do rótulo do campo, do KPI ou da coluna, e uma bolha com a explicação.
 *
 * ## Por que existe
 *
 * O diagnóstico de 25/10/2026 contou cerca de 1.330 textos auxiliares fixos nas telas. Uma
 * parte é o que a pessoa precisa ver para não errar, e fica (D-203, "manter visível"). Outra
 * parte explica como a tela funciona para quem quer entender, e era isso que enchia os
 * formulários de linhas cinzas. Essa parte mora aqui. O ⓘ "Como ler esta página"
 * (`BotaoDeInfo`) continua sendo a ajuda da PÁGINA; esta é a do grupo e, por exceção, a do
 * campo. Nunca uma por campo em sequência.
 *
 * ## O que ela reaproveita, e o que ela evita
 *
 * - A bolha é o `PainelFlutuante`: mede o ícone, vira para cima quando não cabe, prende à
 *   janela, acompanha a rolagem (inclusive a do corpo do modal) e é portada para o diálogo
 *   do topo. Assim ela não é cortada por tabela com rolagem, nem por modal, e não nasce
 *   inerte quando há diálogo aberto.
 * - Ela NÃO entra na pilha da `Sobreposicao`: entrar tornaria a página inteira inerte e
 *   travaria a rolagem por causa de uma frase.
 * - O Esc é consumido em CAPTURA, antes da escuta do diálogo, como fazem o seletor, a
 *   multisseleção e o calendário: dentro de um modal, o primeiro Esc fecha só a dica.
 *
 * ## O texto mora num nó só
 *
 * Fechada, a dica deixa o texto num `<span hidden>`; aberta, a bolha carrega o MESMO id.
 * O `aria-describedby` do botão (e o do campo, quando a dica é de campo) aponta para esse
 * id nos dois estados, então quem usa leitor de tela ouve a explicação ao focar, e o texto
 * nunca existe duas vezes no DOM. Sem `role` de tooltip, diálogo ou status: a bolha é
 * descrição, não região viva.
 *
 * ## Como abre e fecha
 *
 * - Mouse (`hover: hover` e `pointer: fine`): parar ~400 ms sobre o ícone abre uma prévia, e
 *   ela continua aberta enquanto o ponteiro estiver no ícone ou na bolha (200 ms para
 *   atravessar o vão). O clique fixa.
 * - Toque: o toque abre e fecha; não depende de hover.
 * - Teclado: Tab chega ao ícone, Enter ou Espaço abre e fecha, Esc fecha e deixa o foco no
 *   ícone, Tab para fora fecha. O foco sozinho não abre (WCAG 3.2.1).
 * - Clique fora, outra dica aberta ou um diálogo novo por cima fecham.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Icone } from '../shell/icone.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DA_DICA } from './dica-contextual-textos.ts'
import { useConsultaDeTela } from './indice-lateral.tsx'
import { PainelFlutuante, foraDoConjunto, usePosicaoDoPainel } from './painel-flutuante.tsx'
import { useDestinoDosAvisos } from './sobreposicao.tsx'

/** Espera do mouse parado antes da prévia: passar por cima a caminho de outro lugar não abre nada. */
export const ESPERA_DA_PREVIA_MS = 400
/** Tolerância para o ponteiro atravessar o vão entre o ícone e a bolha (WCAG 1.4.13). */
export const TOLERANCIA_DE_SAIDA_MS = 200
/** Largura máxima da bolha; a geometria ainda a prende à janela. */
const LARGURA_MAXIMA = 288
const LARGURA_MINIMA = 176
const ALTURA_DESEJADA = 220

/**
 * Uma dica aberta por vez, no app inteiro: abrir uma fecha a anterior. Um registro de
 * módulo, como a pilha de sobreposições, porque as dicas não se conhecem.
 */
let fecharADicaAberta: (() => void) | null = null

type Estado = 'fechada' | 'previa' | 'fixa'

/** Largura pela quantidade de texto: uma frase curta não precisa de uma bolha de 288 px. */
function larguraPeloTexto(conteudo: ReactNode): number {
  if (typeof conteudo !== 'string') return LARGURA_MAXIMA
  return Math.max(LARGURA_MINIMA, Math.min(LARGURA_MAXIMA, Math.round(conteudo.length * 6.4) + 28))
}

export function DicaContextual({
  rotulo,
  nome,
  children,
  id,
  alinhar = 'inicio',
  foraDoTab = false,
  teste,
}: {
  /** De que é a dica: o título do grupo, o rótulo do campo ou do KPI. Vira "Sobre {rotulo}". */
  rotulo: string
  /** Nome acessível inteiro, quando "Sobre {rotulo}" não serve (o motivo de indisponibilidade usa). */
  nome?: string
  /** Uma a três frases curtas. Texto, sem link nem controle: a bolha não recebe foco. */
  children: ReactNode
  /** O id do texto, quando um campo já o referencia no `aria-describedby` dele. */
  id?: string
  /** `fim` quando o ícone fica na borda direita: a bolha cresce para a esquerda. */
  alinhar?: 'inicio' | 'fim'
  /**
   * Fora da ordem do Tab, para a grade em que cada célula teria uma dica (a 8.2): o Tab segue de
   * célula em célula, e a dica continua no mouse, no toque e na descrição do que ela explica.
   */
  foraDoTab?: boolean
  teste?: string
}): React.JSX.Element {
  const texto = useTextos(TEXTOS_DA_DICA)
  const automatico = useId()
  const idDoTexto = id ?? `${automatico}-dica`
  const [estado, setEstado] = useState<Estado>('fechada')
  const aberta = estado !== 'fechada'
  const botao = useRef<HTMLButtonElement>(null)
  const caixa = useRef<HTMLSpanElement>(null)
  const temporizador = useRef<number | undefined>(undefined)
  const comMouse = useConsultaDeTela('(hover: hover) and (pointer: fine)')
  const destino = useDestinoDosAvisos()

  const limparTemporizador = (): void => {
    window.clearTimeout(temporizador.current)
    temporizador.current = undefined
  }

  const fechar = useCallback((): void => {
    window.clearTimeout(temporizador.current)
    temporizador.current = undefined
    setEstado('fechada')
  }, [])

  const abrir = (como: 'previa' | 'fixa'): void => {
    if (fecharADicaAberta !== null && fecharADicaAberta !== fechar) fecharADicaAberta()
    fecharADicaAberta = fechar
    setEstado(como)
  }

  // A dica que sai da tela deixa de ser "a aberta".
  useEffect(() => () => {
    window.clearTimeout(temporizador.current)
    if (fecharADicaAberta === fechar) fecharADicaAberta = null
  }, [fechar])

  // Um diálogo que abre (ou fecha) por cima muda o topo da pilha: a dica de baixo fecha.
  const destinoAoAbrir = useRef(destino)
  useEffect(() => {
    if (!aberta) {
      destinoAoAbrir.current = destino
      return
    }
    if (destinoAoAbrir.current !== destino) fechar()
  }, [aberta, destino, fechar])

  // Esc em captura (antes do diálogo) e clique fora do conjunto ícone + bolha.
  useEffect(() => {
    if (!aberta) return undefined
    const noEscape = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return
      // Uma lista de seletor ou um calendário aberto já tratou este Esc: ele é deles.
      if (evento.defaultPrevented) return
      // Debaixo de um diálogo novo, o Esc é dele.
      if (botao.current?.closest('[inert]') != null) return
      evento.preventDefault()
      evento.stopPropagation()
      // O foco só volta ao ícone se estava nele (ou perdido): a prévia do mouse abre sem foco,
      // e quem digitava num campo continua no campo.
      const focoNoIcone = document.activeElement === botao.current || document.activeElement === document.body
      fechar()
      if (focoNoIcone) botao.current?.focus()
    }
    const foraDaDica = (evento: MouseEvent | TouchEvent): void => {
      if (foraDoConjunto(evento.target, caixa.current)) fechar()
    }
    document.addEventListener('keydown', noEscape, true)
    document.addEventListener('mousedown', foraDaDica)
    document.addEventListener('touchstart', foraDaDica, { passive: true })
    return () => {
      document.removeEventListener('keydown', noEscape, true)
      document.removeEventListener('mousedown', foraDaDica)
      document.removeEventListener('touchstart', foraDaDica)
    }
  }, [aberta, fechar])

  const geometria = usePosicaoDoPainel(aberta, () => botao.current?.getBoundingClientRect() ?? null, {
    alturaDesejada: ALTURA_DESEJADA,
    largura: larguraPeloTexto(children),
    alinhar,
  })

  const aoEntrar = (): void => {
    if (!comMouse) return
    limparTemporizador()
    if (aberta) return
    temporizador.current = window.setTimeout(() => { abrir('previa') }, ESPERA_DA_PREVIA_MS)
  }
  const aoSair = (): void => {
    if (!comMouse) return
    limparTemporizador()
    if (estado !== 'previa') return
    temporizador.current = window.setTimeout(fechar, TOLERANCIA_DE_SAIDA_MS)
  }

  return (
    <span ref={caixa} className="relative inline-flex shrink-0 items-center align-middle">
      <button
        ref={botao}
        type="button"
        className="dv-dica relative inline-grid h-6 w-6 -my-1 place-items-center rounded-full text-faint transition-colors hover:bg-navy/[.06] hover:text-navy aria-expanded:bg-navy/[.06] aria-expanded:text-navy before:absolute before:-inset-x-2.5 before:-top-2.5 before:bottom-0 before:content-[''] md:before:inset-0"
        {...(foraDoTab ? { tabIndex: -1 } : {})}
        aria-label={nome ?? `${texto('sobre')} ${rotulo}`}
        aria-expanded={aberta}
        aria-controls={aberta ? idDoTexto : undefined}
        aria-describedby={idDoTexto}
        data-teste={teste ?? 'dica-contextual'}
        onClick={() => {
          limparTemporizador()
          if (estado === 'fixa') fechar()
          else abrir('fixa')
        }}
        onMouseEnter={aoEntrar}
        onMouseLeave={aoSair}
        onBlur={(evento) => {
          // O foco foi para OUTRO controle (Tab, ou clique num campo): fecha. Clicar no texto
          // da bolha não tem para onde levar o foco (`relatedTarget` nulo), e quem decide esse
          // caso é o clique fora, que reconhece a bolha portada.
          const para = evento.relatedTarget
          if (para === null) return
          if (para instanceof Node && caixa.current?.contains(para) === true) return
          fechar()
        }}
      >
        <Icone nome="info" className="h-3.5 w-3.5" />
      </button>
      {aberta ? (
        <PainelFlutuante
          geometria={geometria}
          id={idDoTexto}
          data-teste="dica-contextual-bolha"
          className="z-[80] overflow-y-auto rounded-[10px] border border-hairline bg-white px-3 py-2 text-left text-[12px] font-normal normal-case leading-snug tracking-normal text-text2 shadow-card"
          onMouseEnter={limparTemporizador}
          onMouseLeave={aoSair}
        >
          {children}
        </PainelFlutuante>
      ) : (
        <span id={idDoTexto} hidden>
          {children}
        </span>
      )}
    </span>
  )
}

/**
 * O título de um `<fieldset>` com a dica do grupo (D-203).
 *
 * O ícone não pode ficar no texto que nomeia o grupo: o `textContent` da legenda (que o censo
 * estrutural lê pela classe `eyebrow`) passaria a carregar o texto oculto da dica, e o nome
 * acessível do grupo ganharia "Sobre …". Por isso o título é um `<span>` com id, e quem chama
 * põe `aria-labelledby={id}` no `<fieldset>`: o grupo continua se chamando só pelo título.
 */
export function LegendaComDica({
  id,
  titulo,
  ajuda,
  classe = 'eyebrow text-muted',
}: {
  /** O id do título; o `<fieldset>` aponta para ele com `aria-labelledby`. */
  id: string
  titulo: string
  ajuda: ReactNode
  /** A classe do título, a mesma que a legenda tinha (`eyebrow text-muted` na maioria). */
  classe?: string
}): React.JSX.Element {
  return (
    <legend className="flex items-center gap-1 px-1">
      <span id={id} className={classe}>{titulo}</span>
      <DicaContextual rotulo={titulo}>{ajuda}</DicaContextual>
    </legend>
  )
}

/**
 * Uma ação indisponível com o motivo ao alcance do toque e do teclado (D-203, item 5).
 *
 * O botão continua `disabled`: nada muda na regra de acionamento (mouse, teclado e
 * automação continuam sem acioná-lo). O `title` de quem chama pode ficar como reforço para
 * o mouse; o que muda é que o motivo deixa de existir só nele, que não aparece no toque nem
 * no foco. Quando a pessoa PRECISA do motivo para seguir, ele vai visível ao lado, e não
 * aqui.
 */
export function ComMotivo({
  acao,
  motivo,
  deQue,
  children,
  alinhar = 'fim',
  teste,
}: {
  /** O nome da ação, como o botão a mostra ("Emitir NF"). */
  acao: string
  /** Sem motivo (ação disponível), só o botão aparece. */
  motivo: string | null | undefined
  /** De qual linha é a ação, quando ela se repete numa tabela ("parcela 2/3"): entra no nome. */
  deQue?: string
  children: ReactNode
  alinhar?: 'inicio' | 'fim'
  teste?: string
}): React.JSX.Element {
  const texto = useTextos(TEXTOS_DA_DICA)
  if (motivo === null || motivo === undefined || motivo === '') return <>{children}</>
  return (
    <span className="inline-flex items-center gap-0.5">
      {children}
      <DicaContextual
        rotulo={acao}
        nome={`${texto('indisponivel')} ${acao}${deQue === undefined ? '' : `, ${deQue}`}`}
        alinhar={alinhar}
        {...(teste === undefined ? {} : { teste })}
      >
        {motivo}
      </DicaContextual>
    </span>
  )
}
